<template>
  <div class="integration-detail-grid">
    <div class="integration-stack">
      <DetailCard title="Provider call">
        <Metadata :fields="metadata" />
      </DetailCard>
      <DetailCard v-if="contentFields" title="Saved message">
        <Metadata :fields="contentFields" />
      </DetailCard>
      <DetailCard v-if="detail.recipients && detail.recipients.length" title="Recipient results">
        <article v-for="recipient in detail.recipients" :key="recipient.address" class="integration-item">
          <Metadata
            :fields="{
              Recipient: recipient.address,
              Delivered: recipient.delivered,
              'Delivered at': date(recipient.delivered_at, true),
              Opened: recipient.opened,
              'Opened at': date(recipient.opened_at, true),
            }"
          />
        </article>
      </DetailCard>
    </div>
    <div class="integration-stack">
      <PayloadViewer v-if="detail.request !== null && detail.request !== undefined" title="Request" :payload="snapshot(detail.request)" />
      <PayloadViewer v-if="detail.response !== null && detail.response !== undefined" title="Response" :payload="snapshot(detail.response)" />
      <DetailCard v-if="detail.message && detail.message.html" title="Saved HTML preview">
        <p class="integration-muted integration-preview-note">
          External images and interactive content are disabled.
        </p>
        <iframe
          class="integration-html"
          tabindex="0"
          sandbox=""
          referrerpolicy="no-referrer"
          title="Saved email HTML preview"
          :srcdoc="html"
        />
      </DetailCard>
      <DetailCard v-else-if="!hasPayload" title="Provider payload">
        <p class="integration-muted integration-empty-payload">
          No request or response payload was saved for this record.
        </p>
      </DetailCard>
    </div>
  </div>
</template>

<script>
import Metadata from './Metadata'
import PayloadViewer from './PayloadViewer'
import DetailCard from './DetailCard'
import { dateLabel, humanize, recipientLabel, templateLabel, safeHtml, messageText } from './model'

export default {
  components: { Metadata, PayloadViewer, DetailCard },
  props: { detail: { type: Object, required: true }, integration: { type: String, required: true } },
  computed: {
    metadata() {
      const value = this.detail
      const common = {
        'Created': dateLabel(value.created_at, true),
        'Method': humanize(value.method),
        'Destination': recipientLabel(value.destination),
        'Account': value.account?.label,
        'Result': value.result?.label,
        'Result message': value.result?.message,
        'HTTP status': value.http_status
      }
      if (this.integration === 'knowledge_ai') return {
        ...common,
        'Operation': value.operation_id,
        'Attempt': value.sequence,
        'Duration (ms)': value.duration_ms,
        'Started': dateLabel(value.timing?.started_at, true),
        'Finished': dateLabel(value.timing?.finished_at, true)
      }
      if (this.integration === 'telegram') return { ...common, 'Message ID': value.message_id }
      if (this.integration === 'whatsapp') return {
        ...common,
        'Provider message ID': value.provider_message_id,
        'Provider ticket ID': value.provider_ticket_id,
        'Provider response ID': value.provider_response_id,
        'Error code': value.error_code,
        'Started': dateLabel(value.timing?.started_at, true),
        'Finished': dateLabel(value.timing?.finished_at, true),
        'Accepted': dateLabel(value.timing?.accepted_at, true),
        'Delivered': dateLabel(value.timing?.delivered_at, true),
        'Read': dateLabel(value.timing?.read_at, true)
      }
      if (this.integration === 'email') return {
        ...common,
        'Sender': value.sender,
        'Subject': value.subject,
        'Attempt sequence': value.sequence,
        'Provider message ID': value.provider_message_id,
        'Error code': value.error_code,
        'Started': dateLabel(value.timing?.started_at, true),
        'Finished': dateLabel(value.timing?.finished_at, true)
      }
      return {
        ...common,
        'Sender': value.sender,
        'Subject': value.subject,
        'Template': templateLabel(value.template),
        'Response GUID': value.response_guid
      }
    },
    contentFields() {
      const value = this.detail
      let fields = null
      if (this.integration === 'whatsapp') {
        fields = { Message: messageText(value.content_preview), Template: templateLabel(value.template) }
      } else if (this.integration === 'email' && value.message) {
        fields = {
          'Text': messageText(value.message.text),
          'Message public ID': value.message.public_id,
          'RFC message ID': value.message.rfc_message_id,
          'Message provider ID': value.message.provider_message_id,
          'Reply to': value.message.reply_to
        }
      }
      return fields && Object.values(fields).some(item => item !== null && item !== undefined && item !== '—') ? fields : null
    },
    html() { return safeHtml(this.detail.message?.html || '') },
    hasPayload() {
      return ['request', 'response'].some(key => this.detail[key] !== null && this.detail[key] !== undefined)
    }
  },
  methods: {
    date: dateLabel,
    snapshot(value) { return { availability: 'available', format: 'json', value } }
  }
}
</script>
